/* eslint-disable react-refresh/only-export-components */
import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';

import { LoadingSpinner } from './components/Loading/LoadingSpinner';
import { LoginPage } from './components/Auth/LoginPage';

import { ComingSoonPage } from './components/Common/ComingSoonPage';
import { SearchPreservingRedirect } from './components/Common/SearchPreservingRedirect';

// Redirect legacy /communications/:displayId → /ucc/:displayId, preserving query string.
function CommunicationsTicketRedirect() {
  const { displayId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/ucc/${displayId ?? ''}${search}`} replace />;
}

// Generic single-id redirect for legacy bare-aliased CRM detail pages.
// Builds the canonical /crm/<segment>/:id URL while preserving the path
// param and query string. Used for /customers/:id, /companies/:id, etc.
function CrmDetailRedirect({ to }: { to: (id: string) => string }) {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`${to(id ?? '')}${search}`} replace />;
}

function CustomerRedirect() {
  return <CrmDetailRedirect to={(id) => `/crm/customers/${id}`} />;
}
function Customer360Redirect() {
  return <CrmDetailRedirect to={(id) => `/crm/customers/${id}/360`} />;
}
function CompanyRedirect() {
  return <CrmDetailRedirect to={(id) => `/crm/companies/${id}`} />;
}
function CompanyEditRedirect() {
  const { id } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('edit', '1');
  return <Navigate to={`/crm/companies/${id ?? ''}?${params.toString()}`} replace />;
}
function TravelerRedirect() {
  return <CrmDetailRedirect to={(id) => `/crm/travelers/${id}`} />;
}

// Public pages (no auth required)
const ClientPortal = lazy(() => import('./pages/ClientPortal').then(m => ({ default: m.ClientPortal })));
const TripStatusPortal = lazy(() => import('./pages/TripStatusPortal').then(m => ({ default: m.TripStatusPortal })));
const BookingPortal = lazy(() => import('./pages/BookingPortal').then(m => ({ default: m.BookingPortal })));
const PaymentRequestPortal = lazy(() => import('./pages/PaymentRequestPortal').then(m => ({ default: m.PaymentRequestPortal })));

const MagicLinkVerification = lazy(() => import('./components/Auth/MagicLinkVerification').then(m => ({ default: m.MagicLinkVerification })));

// Lazy load components with named exports handling
// This pattern ensures we correctly extract the named export for React.lazy
const DashboardPage = lazy(() => import('./components/Dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const CustomerLocationDashboard = lazy(() => import('./components/Dashboard/CustomerLocationDashboard').then(m => ({ default: m.CustomerLocationDashboard })));

// AI

const UnifiedAIPageV3 = lazy(() => import('./components/AI/UnifiedAIPageV3').then(m => ({ default: m.UnifiedAIPageV3 })));
const TeamAiVisualManualPage = lazy(() => import('./pages/TeamAiVisualManualPage').then(m => ({ default: m.TeamAiVisualManualPage })));
const AIThreadsPage = lazy(() => import('./components/AI/AIThreadsPage').then(m => ({ default: m.AIThreadsPage })));
const PNRConverterPage = lazy(() => import('./components/PNR/PNRConverterPage').then(m => ({ default: m.PNRConverterPage })));
const PNRCreationAgent = lazy(() => import('./components/Agent/PNRCreationAgent').then(m => ({ default: m.PNRCreationAgent })));
const AIUsageStatsPage = lazy(() => import('./components/Admin/AIUsageStatsPage').then(m => ({ default: m.AIUsageStatsPage })));

// PNR & Bookings
const PNRManagerPage = lazy(() => import('./components/PNR/PNRManagerPage').then(m => ({ default: m.PNRManagerPage })));
const PNRHistoryPage = lazy(() => import('./components/PNR/PNRHistoryPage').then(m => ({ default: m.PNRHistoryPage })));
const PNRRecordPage = lazy(() => import('./components/PNR/PNRRecordPage').then(m => ({ default: m.PNRRecordPage })));
const InterfaceImportPage = lazy(() => import('./components/PNR/InterfaceImportPage').then(m => ({ default: m.InterfaceImportPage })));
const PasteAndBook = lazy(() => import('./components/Booking/PasteAndBook').then(m => ({ default: m.PasteAndBook })));
const UnifiedFlightSearch = lazy(() => import('./components/Booking/UnifiedFlightSearch').then(m => ({ default: m.UnifiedFlightSearch })));
const SearchWorkbench = lazy(() => import('./components/Search/Workbench/SearchWorkbench').then(m => ({ default: m.SearchWorkbench })));
const EnhancedBookingViewPage = lazy(() => import('./components/Bookings/EnhancedBookingViewPage').then(m => ({ default: m.EnhancedBookingViewPage })));
const TicketingDeadlinesPage = lazy(() => import('./components/Deadlines/TicketingDeadlinesPage').then(m => ({ default: m.TicketingDeadlinesPage })));
const PriceComparisonPage = lazy(() => import('./components/PriceComparison/PriceComparisonPage').then(m => ({ default: m.PriceComparisonPage })));
const ClientItineraryComposerPage = lazy(() => import('./features/clientItinerary/ClientItineraryComposerPage').then(m => ({ default: m.ClientItineraryComposerPage })));
const FlightQuoteResearchPage = lazy(() => import('./pages/FlightQuoteResearchPage').then(m => ({ default: m.FlightQuoteResearchPage })));
const PNTObligationQueuePage = lazy(() => import('./components/PNT/PNTObligationQueuePage').then(m => ({ default: m.PNTObligationQueuePage })));
const PntIssuancePlansPage = lazy(() => import('./components/PNT/PntIssuancePlansPage').then(m => ({ default: m.PntIssuancePlansPage })));
const PNTListPage = lazy(() => import('./components/PNT/PNTListPage').then(m => ({ default: m.PNTListPage })));
const PNTAnalyticsDashboard = lazy(() => import('./components/PNT/PNTAnalyticsDashboard').then(m => ({ default: m.PNTAnalyticsDashboard })));
const DailyOutlookPage = lazy(() => import('./components/PNT/DailyOutlookPage').then(m => ({ default: m.DailyOutlookPage })));
const PntDigestSettingsPage = lazy(() => import('./components/PNT/PntDigestSettingsPage').then(m => ({ default: m.PntDigestSettingsPage })));
const InterfaceFileMonitoringPage = lazy(() => import('./components/InterfaceFiles/InterfaceFileMonitoringPage').then(m => ({ default: m.InterfaceFileMonitoringPage })));
const RateHawkBookingsPage = lazy(() => import('./components/Hotel/RateHawkBookingsPage').then(m => ({ default: m.RateHawkBookingsPage })));
const HotelBookingsPage = lazy(() => import('./components/Hotel/HotelBookingsPage').then(m => ({ default: m.HotelBookingsPage })));
const HotelConfirmationEditorPage = lazy(() => import('./components/Hotel/HotelConfirmationEditorPage').then(m => ({ default: m.HotelConfirmationEditorPage })));

// RTW
const RTWPlannerPage = lazy(() => import('./components/RTW/RTWPlannerPage').then(m => ({ default: m.RTWPlannerPage })));
const RTWAgentWorkspace = lazy(() => import('./components/RTW/AgentWorkspace/RTWAgentWorkspace').then(m => ({ default: m.RTWAgentWorkspace })));
const RTWTroubleshooterPage = lazy(() => import('./components/RTW/Troubleshooter/RTWTroubleshooterPage').then(m => ({ default: m.RTWTroubleshooterPage })));
const RTWAssistantPage = lazy(() => import('./components/RTW/Assistant/RTWAssistantPage').then(m => ({ default: m.RTWAssistantPage })));
const FabIntelCoverageMap = lazy(() => import('./components/RTW/FabIntelCoverageMap').then(m => ({ default: m.FabIntelCoverageMap })));
const LoyaltyCalculatorPage = lazy(() => import('./components/Loyalty/LoyaltyCalculatorPage').then(m => ({ default: m.LoyaltyCalculatorPage })));
const SharedLinksAdmin = lazy(() => import('./components/RTW/SharedLinksAdmin').then(m => ({ default: m.SharedLinksAdmin })));
const RtwDeskQueuePage = lazy(() => import('./pages/RtwDeskQueuePage').then(m => ({ default: m.RtwDeskQueuePage })));
const RTWFulfilmentReviewPage = lazy(() => import('./pages/RTW/RTWFulfilmentReviewPage').then(m => ({ default: m.RTWFulfilmentReviewPage })));
const AIInboxPage = lazy(() => import('./components/AIInbox/AIInboxPage').then(m => ({ default: m.AIInboxPage })));
const SabreConsolePage = lazy(() => import('./components/AIInbox/SabreConsolePage').then(m => ({ default: m.SabreConsolePage })));
const TermsTemplatesAdmin = lazy(() => import('./components/Terms/TermsTemplatesAdmin').then(m => ({ default: m.TermsTemplatesAdmin })));
const QuickPointsCalculator = lazy(() => import('./components/Loyalty/QuickPointsCalculator').then(m => ({ default: m.QuickPointsCalculator })));
const TierPointsCalculatorPage = lazy(() => import('./pages/TierPointsCalculatorPage').then(m => ({ default: m.TierPointsCalculatorPage })));

// CRM
const CrmHome = lazy(() => import('./components/CRM/CrmHome').then(m => ({ default: m.CrmHome })));
const CustomerManagementPage = lazy(() => import('./components/CRM/CustomerManagementPage').then(m => ({ default: m.CustomerManagementPage })));
const CustomerDetailPage = lazy(() => import('./components/CRM/CustomerDetailPage').then(m => ({ default: m.CustomerDetailPage })));
const CompanyManagementPage = lazy(() => import('./components/CRM/CompanyManagementPage').then(m => ({ default: m.CompanyManagementPage })));
const CompanyDetailPage = lazy(() => import('./components/CRM/CompanyDetailPage').then(m => ({ default: m.CompanyDetailPage })));
const TravelerManagementPage = lazy(() => import('./components/CRM/TravelerManagementPage').then(m => ({ default: m.TravelerManagementPage })));
const PersonProfilePage = lazy(() => import('./components/CRM/PersonProfilePage').then(m => ({ default: m.PersonProfilePage })));
const TravelerDetailPage = lazy(() => import('./components/CRM/TravelerDetailPage').then(m => ({ default: m.TravelerDetailPage })));
const SupplierManagementPage = lazy(() => import('./components/CRM/SupplierManagementPage').then(m => ({ default: m.SupplierManagementPage })));
const SupplierDetailPage = lazy(() => import('./components/CRM/SupplierDetailPage').then(m => ({ default: m.SupplierDetailPage })));
const SellersAndProvidersPage = lazy(() => import('./pages/SellersAndProvidersPage').then(m => ({ default: m.SellersAndProvidersPage })));
const LeadManagementPage = lazy(() => import('./components/CRM/LeadManagementPage').then(m => ({ default: m.LeadManagementPage })));
const LeadDetailPage = lazy(() => import('./components/CRM/LeadDetailPage').then(m => ({ default: m.LeadDetailPage })));
const CommunicationLogsPage = lazy(() => import('./components/CRM/CommunicationLogsPage').then(m => ({ default: m.CommunicationLogsPage })));
const ContactEnrichmentQueuePage = lazy(() => import('./components/CRM/ContactEnrichmentQueuePage').then(m => ({ default: m.ContactEnrichmentQueuePage })));
const ContactMatchingPage = lazy(() => import('./components/CRM/ContactMatchingPage').then(m => ({ default: m.ContactMatchingPage })));
const WhatsAppNotesPage = lazy(() => import('./components/CRM/WhatsAppNotesPage').then(m => ({ default: m.WhatsAppNotesPage })));
const DocumentVaultPage = lazy(() => import('./components/CRM/DocumentVaultPage').then(m => ({ default: m.DocumentVaultPage })));

// Finance
const FinanceDashboardPage = lazy(() => import('./components/Finance/FinanceDashboardPage').then(m => ({ default: m.FinanceDashboardPage })));
const InvoiceManagementPage = lazy(() => import('./components/Finance/InvoiceManagementPage').then(m => ({ default: m.InvoiceManagementPage })));
const InvoiceTemplateManagementPage = lazy(() => import('./components/Finance/InvoiceTemplateManagementPage').then(m => ({ default: m.InvoiceTemplateManagementPage })));
const CreateTripInvoicePage = lazy(() => import('./components/Invoices/CreateTripInvoicePage').then(m => ({ default: m.CreateTripInvoicePage })));
const InvoiceBuilderPage = lazy(() => import('./components/Invoices/InvoiceBuilderPage').then(m => ({ default: m.InvoiceBuilderPage })));
const PaymentManagementPage = lazy(() => import('./components/Finance/PaymentManagementPage').then(m => ({ default: m.PaymentManagementPage })));
const SupplierSettlementPage = lazy(() => import('./components/Finance/SupplierSettlementPage').then(m => ({ default: m.SupplierSettlementPage })));
const PaymentReconciliationPage = lazy(() => import('./components/Finance/PaymentReconciliationPage').then(m => ({ default: m.PaymentReconciliationPage })));
const CommissionTrackingPage = lazy(() => import('./components/Finance/CommissionTrackingPage').then(m => ({ default: m.CommissionTrackingPage })));
const RefundTrackingPage = lazy(() => import('./components/Finance/RefundWorkspace').then(m => ({ default: m.RefundWorkspace })));
const PNTAgingDashboard = lazy(() => import('./components/Finance/PNTAgingDashboard').then(m => ({ default: m.PNTAgingDashboard })));
const CashFlowProjection = lazy(() => import('./components/Finance/CashFlowProjection').then(m => ({ default: m.CashFlowProjection })));
const BankingDashboardPage = lazy(() => import('./components/banking/BankingDashboardPage').then(m => ({ default: m.BankingDashboardPage })));
const PlaidOAuthReturnPage = lazy(() => import('./components/banking/PlaidOAuthReturnPage').then(m => ({ default: m.PlaidOAuthReturnPage })));
const TransactionCategorizer = lazy(() => import('./components/banking/TransactionCategorizer').then(m => ({ default: m.TransactionCategorizer })));
const MerchantRulesManager = lazy(() => import('./components/banking/MerchantRulesManager').then(m => ({ default: m.MerchantRulesManager })));
const CategoryManager = lazy(() => import('./components/banking/CategoryManager').then(m => ({ default: m.CategoryManager })));
const BankingAnalyticsDashboard = lazy(() => import('./components/banking/BankingAnalyticsDashboard').then(m => ({ default: m.BankingAnalyticsDashboard })));
const ReconciliationReviewPage = lazy(() => import('./components/banking/ReconciliationReviewPage').then(m => ({ default: m.ReconciliationReviewPage })));
const TransactionResolutionPage = lazy(() => import('./components/banking/TransactionResolutionPage').then(m => ({ default: m.TransactionResolutionPage })));
const TerminalPage = lazy(() => import('./pages/Finance/Terminal').then(m => ({ default: m.TerminalPage })));
const MotoTerminalPage = lazy(() => import('./pages/Finance/Terminal').then(m => ({ default: m.MotoTerminalPage })));
const SupplierInvoiceUploadPage = lazy(() => import('./components/Finance/SupplierInvoiceUploadPage').then(m => ({ default: m.SupplierInvoiceUploadPage })));
const StatementReconciliationPage = lazy(() => import('./components/Finance/StatementReconciliationPage').then(m => ({ default: m.StatementReconciliationPage })));
const SupplierCreditDashboard = lazy(() => import('./components/Finance/SupplierCreditDashboard').then(m => ({ default: m.SupplierCreditDashboard })));
const SupplierConfigurationPage = lazy(() => import('./components/Finance/SupplierConfigurationPage').then(m => ({ default: m.SupplierConfigurationPage })));
const PaymentSourcesPage = lazy(() => import('./components/Finance/PaymentSourcesPage').then(m => ({ default: m.PaymentSourcesPage })));
const StripeBalanceSyncPage = lazy(() => import('./components/Finance/StripeBalanceSyncPage').then(m => ({ default: m.StripeBalanceSyncPage })));
const StripeSettlementWorkbenchPage = lazy(() => import('./components/Finance/StripeSettlementWorkbenchPage').then(m => ({ default: m.StripeSettlementWorkbenchPage })));
const PaymentEconomicsWorkbenchPage = lazy(() => import('./components/Finance/PaymentEconomicsWorkbenchPage').then(m => ({ default: m.PaymentEconomicsWorkbenchPage })));
const StripeCustomerSyncPage = lazy(() => import('./components/Finance/StripeCustomerSyncPage').then(m => ({ default: m.StripeCustomerSyncPage })));
const CardVaultFopPage = lazy(() => import('./pages/CardVaultFopPage').then(m => ({ default: m.CardVaultFopPage })));
const PaymentLinksAndManageBookingPage = lazy(() => import('./pages/PaymentLinksAndManageBookingPage').then(m => ({ default: m.PaymentLinksAndManageBookingPage })));

// Automation
const QueueMonitoringDashboard = lazy(() => import('./components/Automation/QueueMonitoringDashboard').then(m => ({ default: m.QueueMonitoringDashboard })));
const QueueManagementDashboard = lazy(() => import('./components/Dashboard/QueueManagementDashboard').then(m => ({ default: m.QueueManagementDashboard })));
const QueueActionDashboard = lazy(() => import('./components/Queue/QueueActionDashboard').then(m => ({ default: m.QueueActionDashboard })));
const QueueActionReport = lazy(() => import('./components/Queue/QueueActionReport').then(m => ({ default: m.QueueActionReport })));
const QueueAutomationPage = lazy(() => import('./components/Queue/QueueAutomationPage').then(m => ({ default: m.QueueAutomationPage })));
const QueueAnalyticsDashboard = lazy(() => import('./components/Analytics/QueueAnalyticsDashboard').then(m => ({ default: m.QueueAnalyticsDashboard })));
const AutomationEnginePage = lazy(() => import('./components/Automation/AutomationEnginePage').then(m => ({ default: m.AutomationEnginePage })));
const ScheduleChangeDashboard = lazy(() => import('./components/Automation/ScheduleChangeDashboard').then(m => ({ default: m.ScheduleChangeDashboard })));
const TTLMonitorDashboard = lazy(() => import('./components/Automation/TTLMonitorDashboard').then(m => ({ default: m.TTLMonitorDashboard })));
const TicketingQueueDashboard = lazy(() => import('./components/Automation/TicketingQueueDashboard').then(m => ({ default: m.TicketingQueueDashboard })));

// Queue Command Center (Unified Queue Management)
const QueueCommandCenter = lazy(() => import('./components/Queue/QueueCommandCenter'));
const OpsCommandCenterPage = lazy(() => import('./pages/OpsCommandCenterPage').then(m => ({ default: m.OpsCommandCenterPage })));
const PrintableWorklistReport = lazy(() => import('./components/Queue/PrintableWorklistReport'));
const ManualsPage = lazy(() => import('./pages/Manuals/ManualsPage'));

// Amadeus
const AmadeusQueueManager = lazy(() => import('./components/Amadeus/AmadeusQueueManager').then(m => ({ default: m.AmadeusQueueManager })));
const AmadeusTerminal = lazy(() => import('./components/Amadeus/AmadeusTerminal').then(m => ({ default: m.AmadeusTerminal })));
const AmadeusAvailabilityPage = lazy(() => import('./components/Amadeus/AmadeusAvailabilityPage').then(m => ({ default: m.AmadeusAvailabilityPage })));
const AmadeusPNRCreationPage = lazy(() => import('./components/Amadeus/AmadeusPNRCreationPage').then(m => ({ default: m.AmadeusPNRCreationPage })));
const AmadeusFareRulesPage = lazy(() => import('./components/Amadeus/AmadeusFareRulesPage').then(m => ({ default: m.AmadeusFareRulesPage })));
const AmadeusAncillaryPage = lazy(() => import('./components/Amadeus/AmadeusAncillaryPage').then(m => ({ default: m.AmadeusAncillaryPage })));
const AmadeusAgentPage = lazy(() => import('./components/Amadeus/AmadeusAgentPage').then(m => ({ default: m.AmadeusAgentPage })));

// Check-In
const CheckInTaskView = lazy(() => import('./components/CheckIn/CheckInTaskView').then(m => ({ default: m.CheckInTaskView })));

// Sabre GDS
const SabreAvailabilityPage = lazy(() => import('./components/Sabre/SabreAvailabilityPage').then(m => ({ default: m.SabreAvailabilityPage })));
const SabrePNRCreationPage = lazy(() => import('./components/Sabre/SabrePNRCreationPage').then(m => ({ default: m.SabrePNRCreationPage })));
const SabreFareRulesPage = lazy(() => import('./components/Sabre/SabreFareRulesPage').then(m => ({ default: m.SabreFareRulesPage })));
const SabreAncillaryPage = lazy(() => import('./components/Sabre/SabreAncillaryPage').then(m => ({ default: m.SabreAncillaryPage })));
const SabreQueueAutomationPage = lazy(() => import('./components/Sabre/SabreQueueAutomationPage').then(m => ({ default: m.SabreQueueAutomationPage })));
const SabreCrypticPage = lazy(() => import('./components/Sabre/SabreCrypticPage').then(m => ({ default: m.SabreCrypticPage })));
const SabreIntegrationDashboard = lazy(() => import('./components/Sabre/SabreIntegrationDashboard').then(m => ({ default: m.SabreIntegrationDashboard })));
const DestinationFinderPage = lazy(() => import('./components/Sabre/DestinationFinderPage').then(m => ({ default: m.DestinationFinderPage })));
const PNRSplitPage = lazy(() => import('./components/Sabre/PNRSplitPage').then(m => ({ default: m.PNRSplitPage })));
const SabreSessionDiagnosticsPage = lazy(() => import('./components/Admin/SabreSessionDiagnosticsPage').then(m => ({ default: m.SabreSessionDiagnosticsPage })));
const ExchangeStudioPage = lazy(() => import('./pages/ExchangeStudioPage').then(m => ({ default: m.ExchangeStudioPage })));

// Duffel
const DuffelSearchPage = lazy(() => import('./components/Duffel/DuffelSearchPage').then(m => ({ default: m.DuffelSearchPage })));

// Hotels & Cars
const HotelSearchPage = lazy(() => import('./components/Hotel/HotelSearchPage').then(m => ({ default: m.HotelSearchPage })));
const RateHawkSearchPage = lazy(() => import('./components/Hotel/RateHawkSearchPage').then(m => ({ default: m.RateHawkSearchPage })));
const UnifiedHotelSearchPage = lazy(() => import('./components/Hotel/UnifiedHotelSearchPage').then(m => ({ default: m.UnifiedHotelSearchPage })));
const HotelAutomationPage = lazy(() => import('./pages/HotelAutomationPage').then(m => ({ default: m.HotelAutomationPage })));
const HotelNightsAnalyticsPage = lazy(() => import('./pages/HotelNightsAnalyticsPage').then(m => ({ default: m.HotelNightsAnalyticsPage })));
const CarSearchPage = lazy(() => import('./components/Car/CarSearchPage').then(m => ({ default: m.CarSearchPage })));

// Propeller Travel (Public Platform)
const PropellerFlightSearchPage = lazy(() => import('./components/PropellerTravel/FlightSearchPage').then(m => ({ default: m.FlightSearchPage })));
const PropellerSeatMapPage = lazy(() => import('./components/PropellerTravel/SeatMapPage').then(m => ({ default: m.SeatMapPage })));
const PropellerAvailabilityAlertsPage = lazy(() => import('./components/PropellerTravel/AvailabilityAlertsPage').then(m => ({ default: m.AvailabilityAlertsPage })));
const PropellerFLIFOPage = lazy(() => import('./components/PropellerTravel/FLIFOPage').then(m => ({ default: m.FLIFOPage })));
const PropellerMCTCheckerPage = lazy(() => import('./components/PropellerTravel/MCTCheckerPage').then(m => ({ default: m.MCTCheckerPage })));
const AwardSearchPage = lazy(() => import('./components/PropellerTravel/AwardSearchPage').then(m => ({ default: m.AwardSearchPage })));
const PropellerSettingsPage = lazy(() => import('./components/PropellerTravel/PropellerSettingsPage').then(m => ({ default: m.PropellerSettingsPage })));
const PropellerAvailabilityPage = lazy(() => import('./pax/tools/PaxAvailabilityPage'));
const PropellerFareQuotePage = lazy(() => import('./pax/tools/PaxFareQuotePage'));
const PropellerPriceExplorerPage = lazy(() => import('./pax/tools/PaxPriceExplorerPage'));
const PropellerPaxSeatMapPage = lazy(() => import('./pax/tools/PaxSeatMapPage'));
const PropellerProfilePage = lazy(() => import('./pax/profile/PaxProfile'));

// Agent-only Steel-backed surfaces — sit on the agent dashboard, not under /propeller (public).
const AgentCommandConsole = lazy(() => import('./components/PropellerTravel/AgentCommandConsole').then(m => ({ default: m.AgentCommandConsole })));
const SupplierPortalFlightSearch = lazy(() => import('./components/PropellerTravel/SupplierPortalFlightSearch').then(m => ({ default: m.SupplierPortalFlightSearch })));

// Reports — unified Reporting Center
const ReportingCenter = lazy(() => import('./pages/Reports/ReportingCenter').then(m => ({ default: m.ReportingCenter })));

// Revenue & Analytics
const RevenueManagementDashboard = lazy(() => import('./components/Revenue/RevenueManagementDashboard').then(m => ({ default: m.RevenueManagementDashboard })));
const RateHawkPaymentDashboard = lazy(() => import('./components/Finance/RateHawkPaymentDashboard').then(m => ({ default: m.RateHawkPaymentDashboard })));
const CommissionEngine = lazy(() => import('./components/Finance/CommissionEngine').then(m => ({ default: m.CommissionEngine })));
const ForaIntegrationPage = lazy(() => import('./components/Finance/Fora/ForaIntegrationPage').then(m => ({ default: m.ForaIntegrationPage })));

// Risk & Alerts
const TravelRiskDashboard = lazy(() => import('./components/Risk/TravelRiskDashboard').then(m => ({ default: m.TravelRiskDashboard })));
const FlightAlertsSystem = lazy(() => import('./components/Alerts/FlightAlertsSystem').then(m => ({ default: m.FlightAlertsSystem })));

// Documents & Communication
const DocumentManager = lazy(() => import('./components/Documents/DocumentManager').then(m => ({ default: m.DocumentManager })));

// CRM Extensions
const Client360View = lazy(() => import('./components/CRM/Client360View').then(m => ({ default: m.Client360View })));

// Expenses & Compliance
const ExpenseManager = lazy(() => import('./components/Expenses/ExpenseManager').then(m => ({ default: m.ExpenseManager })));
const AuditTrail = lazy(() => import('./components/Compliance/AuditTrail').then(m => ({ default: m.AuditTrail })));

// Search
const AdvancedSearch = lazy(() => import('./components/Search/AdvancedSearch').then(m => ({ default: m.AdvancedSearch })));

// Supplier Management
const SupplierManager = lazy(() => import('./components/Suppliers/SupplierManager').then(m => ({ default: m.SupplierManager })));

// Admin Extensions
const LoyaltyProgramsPage = lazy(() => import('./components/Admin/LoyaltyProgramsPage').then(m => ({ default: m.LoyaltyProgramsPage })));
const EarningRulesPage = lazy(() => import('./components/Admin/EarningRulesPage').then(m => ({ default: m.EarningRulesPage })));
const PerkProgramAdmin = lazy(() => import('./components/Admin/PerkProgramAdmin').then(m => ({ default: m.PerkProgramAdmin })));
const PaymentMethodsTermsPage = lazy(() => import('./components/Finance/PaymentMethodsTermsPage').then(m => ({ default: m.PaymentMethodsTermsPage })));
const ClientChargesPage = lazy(() => import('./components/Finance/ClientChargesPage').then(m => ({ default: m.ClientChargesPage })));
const CalculationTesterPage = lazy(() => import('./components/Admin/CalculationTesterPage').then(m => ({ default: m.CalculationTesterPage })));
const AuditLogPage = lazy(() => import('./components/Admin/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const RBACManagementPage = lazy(() => import('./components/Admin/RBACManagementPage').then(m => ({ default: m.RBACManagementPage })));
const EarningChartEditor = lazy(() => import('./components/Admin/EarningChartEditor').then(m => ({ default: m.EarningChartEditor })));
const WebScraperManager = lazy(() => import('./components/Admin/WebScraperManager').then(m => ({ default: m.WebScraperManager })));
const LoginScreenCMS = lazy(() => import('./components/Admin/LoginScreenCMS').then(m => ({ default: m.LoginScreenCMS })));
const PNRCompletionRulesPage = lazy(() => import('./components/Admin/PNRCompletionRulesPage').then(m => ({ default: m.PNRCompletionRulesPage })));
const BookingQueueMonitor = lazy(() => import('./components/Admin/BookingQueueMonitor').then(m => ({ default: m.BookingQueueMonitor })));
const PnrTaxonomyAdminPage = lazy(() => import('./components/Admin/PnrTaxonomyAdminPage').then(m => ({ default: m.PnrTaxonomyAdminPage })));
const SupplierPollerMonitor = lazy(() => import('./components/Admin/SupplierPollerMonitor').then(m => ({ default: m.SupplierPollerMonitor })));
const PlatformEventsPage = lazy(() => import('./components/Admin/PlatformEventsPage').then(m => ({ default: m.PlatformEventsPage })));
const GdsRemarkReviewPage = lazy(() => import('./components/Admin/GdsRemarkReviewPage').then(m => ({ default: m.GdsRemarkReviewPage })));
const EmailDomainRepairsPage = lazy(() => import('./components/Admin/EmailDomainRepairsPage').then(m => ({ default: m.EmailDomainRepairsPage })));
const ManagedListsPage = lazy(() => import('./components/Admin/ManagedListsPage').then(m => ({ default: m.ManagedListsPage })));
const AutomationsPage = lazy(() => import('./pages/Admin/AutomationsPage').then(m => ({ default: m.AutomationsPage })));
const DeliveryPage = lazy(() => import('./pages/Admin/DeliveryPage').then(m => ({ default: m.DeliveryPage })));
const DeskAssistantControlsPage = lazy(() => import('./pages/DeskAssistantControlsPage').then(m => ({ default: m.DeskAssistantControlsPage })));
const SabreSessionPoolPage = lazy(() => import('./pages/SabreSessionPoolPage').then(m => ({ default: m.SabreSessionPoolPage })));
const RtwInviteDraftsPage = lazy(() => import('./pages/RTW/RtwInviteDraftsPage').then(m => ({ default: m.RtwInviteDraftsPage })));
const RtwFiledFareRulesPage = lazy(() => import('./pages/Admin/RtwFiledFareRulesPage').then(m => ({ default: m.RtwFiledFareRulesPage })));
const MorningSettingsPage = lazy(() => import('./components/Settings/MorningSettingsPage').then(m => ({ default: m.MorningSettingsPage })));
const ScheduleChangesSettings = lazy(() => import('./components/Settings/ScheduleChangesSettings').then(m => ({ default: m.ScheduleChangesSettings })));

// Operations
const SmartQueuePage = lazy(() => import('./components/Operations/SmartQueuePage').then(m => ({ default: m.SmartQueuePage })));
const OpsInboxPage = lazy(() => import('./components/Operations/OpsInboxPage').then(m => ({ default: m.OpsInboxPage })));
const OpsInboxWithAssign = lazy(() => import('./components/Operations/OpsInboxWithAssign').then(m => ({ default: m.OpsInboxWithAssign })));
const OpsDispatchWithHeader = lazy(() => import('./components/Operations/OpsDispatchWithHeader').then(m => ({ default: m.OpsDispatchWithHeader })));
const OpsReplyQueuePage = lazy(() => import('./components/Operations/OpsReplyQueuePage').then(m => ({ default: m.OpsReplyQueuePage })));
const OpsIdentityReviewPage = lazy(() => import('./components/Operations/OpsIdentityReviewPage').then(m => ({ default: m.OpsIdentityReviewPage })));
const OpsCustomerCareRequestInboxPage = lazy(() => import('./components/Operations/OpsCustomerCareRequestInboxPage').then(m => ({ default: m.OpsCustomerCareRequestInboxPage })));
const OpsStaleWorkReviewPage = lazy(() => import('./components/Operations/OpsStaleWorkReviewPage').then(m => ({ default: m.OpsStaleWorkReviewPage })));
const TodayPage = lazy(() => import('./components/Operations/Today/TodayPage').then(m => ({ default: m.TodayPage })));
const OpsAwaitingReplyPage = lazy(() => import('./components/Operations/OpsAwaitingReplyPage').then(m => ({ default: m.OpsAwaitingReplyPage })));
const LiftShiftPage = lazy(() => import('./components/LiftShift/LiftShiftPage').then(m => ({ default: m.LiftShiftPage })));
const CallsPage = lazy(() => import('./components/Calls/CallsPage').then(m => ({ default: m.CallsPage })));
const CallsHealthPage = lazy(() => import('./components/Calls/CallsHealthPage').then(m => ({ default: m.CallsHealthPage })));
const PromisesAndPassportExpiryPage = lazy(() => import('./pages/PromisesAndPassportExpiryPage').then(m => ({ default: m.PromisesAndPassportExpiryPage })));
const ItineraryBuilderPage = lazy(() => import('./components/Itinerary/ItineraryBuilderPage').then(m => ({ default: m.ItineraryBuilderPage })));
const AdvancedItineraryBuilder = lazy(() => import('./components/Itinerary/AdvancedItineraryBuilder').then(m => ({ default: m.AdvancedItineraryBuilder })));
const ProposalViewPage = lazy(() => import('./components/Itinerary/ProposalViewPage').then(m => ({ default: m.ProposalViewPage })));
const SabreTerminalPage = lazy(() => import('./components/SabreTerminal/SabreTerminalPage').then(m => ({ default: m.SabreTerminalPage })));

// Support
// FreshdeskDashboardPage uses export default, so we import it directly
const FreshdeskDashboardPage = lazy(() => import('./components/Freshdesk/FreshdeskDashboardPage'));
const FreshdeskTicketSyncPage = lazy(() => import('./components/Freshdesk/FreshdeskTicketSyncPage').then(m => ({ default: m.FreshdeskTicketSyncPage })));
const FreshdeskTicketAnalysisPage = lazy(() => import('./components/Freshdesk/FreshdeskTicketAnalysisPage').then(m => ({ default: m.FreshdeskTicketAnalysisPage })));

// Documents
const PNRDocumentGenerator = lazy(() => import('./components/Documents/PNRDocumentGenerator').then(m => ({ default: m.PNRDocumentGenerator })));

// Settings
const GDSSettingsPage = lazy(() => import('./components/Settings/GDSSettingsPage').then(m => ({ default: m.GDSSettingsPage })));
const SettingsPage = lazy(() => import('./components/Settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const PlatformAccessTokensPage = lazy(() => import('./components/Settings/PlatformAccessTokensPage').then(m => ({ default: m.PlatformAccessTokensPage })));
const ApiDocsPage = lazy(() => import('./components/Settings/ApiDocsPage').then(m => ({ default: m.ApiDocsPage })));
const ForaSyncPage = lazy(() => import('./components/Automations/ForaSyncPage').then(m => ({ default: m.ForaSyncPage })));
const HotelPropertyManagementPage = lazy(() => import('./components/CRM/HotelPropertyManagementPage').then(m => ({ default: m.HotelPropertyManagementPage })));
const ConnectionsSettings = lazy(() => import('./components/Settings/ConnectionsSettings').then(m => ({ default: m.ConnectionsSettings })));
const SabreAgentSettingsPage = lazy(() => import('./components/Settings/SabreAgentSettingsPage').then(m => ({ default: m.SabreAgentSettingsPage })));
const SabreAgentPage = lazy(() => import('./components/Sabre/Agent/SabreAgentPage').then(m => ({ default: m.SabreAgentPage })));
const CommunicationsSettingsPage = lazy(() => import('./components/Settings/CommunicationsSettingsPage').then(m => ({ default: m.CommunicationsSettingsPage })));
const AiPersonasAndShiftsManager = lazy(() => import('./components/Settings/AiPersonasAndShiftsManager').then(m => ({ default: m.AiPersonasAndShiftsManager })));

// Integrations
import { lazyRetry } from './utils/lazyImport';

// ... (keep previous imports)

// Integrations
const IntegrationsManagementPage = lazyRetry(
  () => import('./components/Integrations/IntegrationsManagementPage').then(m => ({ default: m.IntegrationsManagementPage })),
  'IntegrationsManagementPage'
);
const IntegrationDetailPage = lazyRetry(
  () => import('./components/Integrations/IntegrationDetailPage').then(m => ({ default: m.IntegrationDetailPage })),
  'IntegrationDetailPage'
);

// PropLet & VM Management
const PropLetManager = lazy(() => import('./components/AI/PropLetManager').then(m => ({ default: m.PropLetManager })));
const PropLetChatInterface = lazy(() => import('./components/AI/PropLetChatInterface').then(m => ({ default: m.PropLetChatInterface })));
const VMManager = lazy(() => import('./components/AI/VMManager').then(m => ({ default: m.VMManager })));
const LiveSessionView = lazy(() => import('./components/Agent/LiveSessionView').then(m => ({ default: m.default })));

// Integrations
const TodoistIntegrationPage = lazy(() => import('./components/Todoist/TodoistIntegrationPage').then(m => ({ default: m.TodoistIntegrationPage })));
const PipedreamIntegrationPage = lazy(() => import('./components/Integrations/PipedreamIntegrationPage').then(m => ({ default: m.PipedreamIntegrationPage })));

// Admin
const AdminDashboardPage = lazy(() => import('./components/Admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const PortalProfilesPage = lazy(() => import('./components/Admin/PortalProfilesPage').then(m => ({ default: m.PortalProfilesPage })));
const UserManagementPage = lazy(() => import('./components/Admin/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const UserApprovalQueue = lazy(() => import('./components/Admin/UserApprovalQueue').then(m => ({ default: m.UserApprovalQueue })));
const PermissionCenter = lazy(() => import('./components/Admin/PermissionCenter').then(m => ({ default: m.PermissionCenter })));
const EmailTemplatesPage = lazy(() => import('./components/Admin/EmailTemplatesPage').then(m => ({ default: m.EmailTemplatesPage })));
const AirlineClassMappingPage = lazy(() => import('./components/Admin/AirlineClassMappingPage').then(m => ({ default: m.AirlineClassMappingPage })));
const PBXConfigPanel = lazy(() => import('./components/Admin/PBXConfigPanel').then(m => ({ default: m.PBXConfigPanel })));
const PaxBannersAdmin = lazy(() => import('./components/Admin/PaxBannersAdmin').then(m => ({ default: m.PaxBannersAdmin })));
const PaxContentManager = lazy(() => import('./components/Admin/PaxContentManager').then(m => ({ default: m.PaxContentManager })));
const PaxInviteAdmin = lazy(() => import('./components/Admin/PaxInviteAdmin').then(m => ({ default: m.PaxInviteAdmin })));
const FDImportMonitor = lazy(() => import('./pages/Admin/FDImportMonitor'));
const CrmTagsAdmin = lazy(() => import('./pages/Admin/CrmTagsAdmin'));
const SystemManualPage = lazy(() => import('./components/Admin/SystemManual/SystemManualPage').then(m => ({ default: m.SystemManualPage })));
const TicketDetailPage = lazy(() => import('./pages/Communications/TicketDetailPage').then(m => ({ default: m.TicketDetailPage })));
const NewTicketPage = lazy(() => import('./pages/Communications/NewTicketPage').then(m => ({ default: m.NewTicketPage })));

// Analytics
const AgentPerformancePage = lazy(() => import('./components/Analytics/AgentPerformancePage').then(m => ({ default: m.AgentPerformancePage })));

// Templates
const ItineraryTemplatesPage = lazy(() => import('./components/Templates/ItineraryTemplatesPage').then(m => ({ default: m.ItineraryTemplatesPage })));

// Proposals
const ProposalTrackingDashboard = lazy(() => import('./components/Proposals/ProposalTrackingDashboard').then(m => ({ default: m.ProposalTrackingDashboard })));

// Communications
const UnifiedCommsDashboard = lazy(() => import('./components/Communications/UnifiedCommsDashboard').then(m => ({ default: m.UnifiedCommsDashboard })));
const AutomationsSettings = lazy(() => import('./components/Communications/AutomationsSettings').then(m => ({ default: m.AutomationsSettings })));

// Data Quality
const OrphanRecordsDashboard = lazy(() => import('./components/DataQuality/OrphanRecordsDashboard').then(m => ({ default: m.OrphanRecordsDashboard })));

// Consolidators
const ConsolidatorPriceComparisonPage = lazy(() => import('./components/Consolidators/PriceComparisonPage').then(m => ({ default: m.ConsolidatorPriceComparisonPage })));

// Tasks & Actions
const TaskDashboard = lazy(() => import('./components/Tasks/TaskDashboardRoute').then(m => ({ default: m.TaskDashboardRoute })));
const ActionPlanLibrary = lazy(() => import('./components/Actions/ActionPlanLibrary').then(m => ({ default: m.ActionPlanLibrary })));

// Routines & Automation
const RoutinesManager = lazy(() => import('./components/Routines/RoutinesManager').then(m => ({ default: m.RoutinesManager })));

// Document Acknowledgement
const DocumentAcknowledgement = lazy(() => import('./components/Documents/DocumentAcknowledgement').then(m => ({ default: m.DocumentAcknowledgement })));

// Trip Management
const TripManagementPage = lazy(() => import('./components/Trips/TripManagementPage').then(m => ({ default: m.default })));
const TripDetailPage = lazy(() => import('./components/Trips/TripDetailPage').then(m => ({ default: m.default })));
const TripEditPage = lazy(() => import('./components/Trips/TripEditPage').then(m => ({ default: m.default })));
const TripStatementView = lazy(() => import('./components/Trips/TripStatementView').then(m => ({ default: m.TripStatementView })));
const PublicTripStatementPage = lazy(() => import('./components/Trips/PublicTripStatementPage').then(m => ({ default: m.PublicTripStatementPage })));

// Partner Management
const PartnerManagementPage = lazy(() => import('./components/Partners/PartnerManagementPage').then(m => ({ default: m.PartnerManagementPage })));
const PartnerDetailPage = lazy(() => import('./components/Partners/PartnerDetailPage').then(m => ({ default: m.PartnerDetailPage })));
const PartnerOnboardingWizard = lazy(() => import('./components/Partners/PartnerOnboardingWizard').then(m => ({ default: m.PartnerOnboardingWizard })));
const PartnerCommissionNewRoute = lazy(() => import('./components/Partners/PartnerRouteEditors').then(m => ({ default: m.PartnerCommissionNewRoute })));
const PartnerCommissionModelRoute = lazy(() => import('./components/Partners/PartnerRouteEditors').then(m => ({ default: m.PartnerCommissionModelRoute })));
const PartnerPermissionsRoute = lazy(() => import('./components/Partners/PartnerRouteEditors').then(m => ({ default: m.PartnerPermissionsRoute })));
const PartnerPayoutsPage = lazy(() => import('./components/Partners/PartnerPayoutsPage').then(m => ({ default: m.default })));

// General Ledger
const ChartOfAccountsPage = lazy(() => import('./components/Finance/ChartOfAccountsPage').then(m => ({ default: m.ChartOfAccountsPage })));
const JournalEntriesPage = lazy(() => import('./components/Finance/JournalEntriesPage').then(m => ({ default: m.JournalEntriesPage })));

// ========== 25 IMPROVEMENTS PLAN - NEW COMPONENTS ==========

// Task Management System (Feature #16)
const TaskManagementDashboard = lazy(() => import('./components/Tasks/TaskManagementDashboard').then(m => ({ default: m.TaskManagementDashboard })));

// QC Checklists System (Feature #17)
const QCChecklistDashboard = lazy(() => import('./components/QualityControl/QCChecklistDashboard').then(m => ({ default: m.QCChecklistDashboard })));

// AR Aging Dashboard (Feature #11)
const ARAgingDashboard = lazy(() => import('./components/Finance/ARAgingDashboard').then(m => ({ default: m.ARAgingDashboard })));

// AP Management (Feature #12)
const APManagementDashboard = lazy(() => import('./components/Finance/APManagementDashboard').then(m => ({ default: m.APManagementDashboard })));

// Supplier Rates Manager (Feature #7)
const SupplierRatesManager = lazy(() => import('./components/Suppliers/SupplierRatesManager').then(m => ({ default: m.SupplierRatesManager })));

// Agent Performance Dashboard (Feature #23)
const AgentPerformanceDashboard = lazy(() => import('./components/Analytics/AgentPerformanceDashboard').then(m => ({ default: m.AgentPerformanceDashboard })));



// Trip Files Manager (Feature #1)
const TripFilesManager = lazy(() => import('./components/TripFiles/TripFilesManager').then(m => ({ default: m.TripFilesManager })));

// Document Templates Manager (Feature #2)
const DocumentTemplatesManager = lazy(() => import('./components/Templates/DocumentTemplatesManager').then(m => ({ default: m.DocumentTemplatesManager })));

// Email Templates Manager (Feature #3)
const EmailTemplatesManager = lazy(() => import('./components/Templates/EmailTemplatesManager').then(m => ({ default: m.EmailTemplatesManager })));

// Smart Alerts Dashboard (Feature #4)
const SmartAlertsDashboard = lazy(() => import('./components/Alerts/SmartAlertsDashboard').then(m => ({ default: m.SmartAlertsDashboard })));

// Group Booking Manager (Feature #5)
const GroupBookingManager = lazy(() => import('./components/Groups/GroupBookingManager').then(m => ({ default: m.GroupBookingManager })));

// Service Fees Manager (Feature #8)
const ServiceFeesManager = lazy(() => import('./components/Finance/ServiceFeesManager').then(m => ({ default: m.ServiceFeesManager })));

// Payment Plans Manager (Feature #9)
const PaymentPlansManager = lazy(() => import('./components/Finance/PaymentPlansManager').then(m => ({ default: m.PaymentPlansManager })));

// Refund Management Page (Feature #10)

// Revenue Analytics Dashboard (Feature #13)
const RevenueAnalyticsDashboard = lazy(() => import('./components/Revenue/RevenueAnalyticsDashboard').then(m => ({ default: m.RevenueAnalyticsDashboard })));

// Customer Segments Manager (Feature #14)
const CustomerSegmentsManager = lazy(() => import('./components/CRM/CustomerSegmentsManager').then(m => ({ default: m.CustomerSegmentsManager })));

// VIP Support Queue (Feature #16)
const VIPSupportPage = lazy(() => import('./components/CRM/VIPSupportPage'));

// Knowledge Base Admin (Phase 5)
const KBAdminPage = lazy(() => import('./components/KnowledgeBase/KBAdminPage').then(m => ({ default: m.KBAdminPage })));

// Aviation Operations Center (AOC)
const AviationOperationsCenterPage = lazy(() => import('./components/AOC/AviationOperationsCenterPage').then(m => ({ default: m.AviationOperationsCenterPage })));

// SLA Management Dashboard
const SLADashboard = lazy(() => import('./pages/Admin/SLADashboard').then(m => ({ default: m.SLADashboard })));

// System Health Dashboard
const SystemHealthDashboard = lazy(() => import('./pages/Admin/SystemHealthDashboard').then(m => ({ default: m.SystemHealthDashboard })));

// Workforce, Granular Subtask Routing Matrix & Payroll Admin Page
const WorkforceAndPayrollAdminPage = lazy(() => import('./pages/Admin/WorkforceAndPayrollAdminPage').then(m => ({ default: m.WorkforceAndPayrollAdminPage })));

const PublicRegistrationPage = lazy(() => import('./components/Auth/PublicRegistrationPage').then(m => ({ default: m.PublicRegistrationPage })));

import { ProtectedRoute, PropellerProtectedRoute } from './components/Auth/ProtectedRoutes';

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  // Public registration for Propeller Travel
  {
    path: '/register',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PublicRegistrationPage />
      </Suspense>
    ),
  },
  // Passenger Magic Link verification
  {
    path: '/auth/magic',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <MagicLinkVerification />
      </Suspense>
    ),
  },
  // Public client portal route (no auth required)
  {
    path: '/itinerary/:token',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <ClientPortal />
      </Suspense>
    ),
  },
  {
    path: '/proposal/:locator',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <ClientPortal />
      </Suspense>
    ),
  },
  {
    path: '/statement/:token',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PublicTripStatementPage />
      </Suspense>
    ),
  },
  // Public trip status portal (no auth required)
  {
    path: '/trip-status',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <TripStatusPortal />
      </Suspense>
    ),
  },
  // Public booking portal (no auth required) - PNR + surname lookup
  {
    path: '/booking',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <BookingPortal />
      </Suspense>
    ),
  },
  // Public booking portal (no auth required) - share token access
  {
    path: '/booking/:token',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <BookingPortal />
      </Suspense>
    ),
  },
  {
    path: '/pay/:token',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaymentRequestPortal />
      </Suspense>
    ),
  },
  {
    path: '/itinerary/view/:id',
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <ProposalViewPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'dashboard/locations', element: <CustomerLocationDashboard /> },

      // AI
      { path: 'ai-assistant', element: <UnifiedAIPageV3 /> },
      { path: 'ai-hub', element: <UnifiedAIPageV3 /> },

      { path: 'ai/sabre-agent', element: <SabreAgentPage /> },
      { path: 'ai/amadeus-agent', element: <AmadeusAgentPage /> },
      { path: 'ai/threads', element: <AIThreadsPage /> },
      { path: 'ai/pnr-parser', element: <PNRConverterPage /> },
      { path: 'ai/pnr-agent', element: <PNRCreationAgent /> },
      { path: 'ai/stats', element: <AIUsageStatsPage /> },
      { path: 'ai/proplets', element: <PropLetManager /> },
      { path: 'ai/proplets/chat', element: <PropLetChatInterface /> },
      // Steel-backed agent surfaces — natural-language command console + supplier flight search.
      { path: 'ai/agent-console', element: <AgentCommandConsole /> },
      { path: 'ai/supplier-search', element: <SupplierPortalFlightSearch /> },
      { path: 'ai/vms', element: <VMManager /> },
      { path: 'agent/live/:sessionId', element: <LiveSessionView /> },
      { path: 'agent/live', element: <LiveSessionView /> }, // For testing

      // Bookings & Search
      { path: 'search', element: <SearchWorkbench /> },
      // Legacy flat flight-search view kept as a deep link / rollback target.
      { path: 'search/flights', element: <UnifiedFlightSearch /> },
      { path: 'search/advanced', element: <AdvancedSearch /> },
      { path: 'consolidators', element: <ConsolidatorPriceComparisonPage /> },
      { path: 'consolidators/search', element: <ConsolidatorPriceComparisonPage /> },
      { path: 'quick-book', element: <PasteAndBook /> },
      { path: 'price-comparison', element: <PriceComparisonPage /> },
      { path: 'air/itinerary-composer', element: <ClientItineraryComposerPage /> },
      { path: 'air/quote-research', element: <FlightQuoteResearchPage /> },
      { path: 'air/exchange-studio', element: <ExchangeStudioPage /> },
      { path: 'bookings', element: <EnhancedBookingViewPage /> },
      { path: 'bookings/deadlines', element: <TicketingDeadlinesPage /> },
      { path: 'pnr', element: <PNRManagerPage /> },
      { path: 'pnr/new', element: <PNRManagerPage /> },
      { path: 'pnr/record/:id', element: <PNRRecordPage /> }, // legacy GUID URLs — redirected to /pnr/:slug after load
      { path: 'pnr/:slug', element: <PNRRecordPage /> },

      // Hotels
      { path: 'hotels', element: <UnifiedHotelSearchPage /> },
      { path: 'hotels/search', element: <HotelSearchPage /> },
      { path: 'hotels/ratehawk', element: <RateHawkSearchPage /> },
      { path: 'hotels/bookings', element: <HotelBookingsPage /> },
      { path: 'hotels/ratehawk/bookings', element: <RateHawkBookingsPage /> },
      { path: 'hotels/ratehawk/payments', element: <RateHawkPaymentDashboard /> },
      { path: 'hotels/:hotelBookingId/confirmation', element: <HotelConfirmationEditorPage /> },
      { path: 'hotels/confirmation', element: <HotelConfirmationEditorPage /> },
      { path: 'hotels/automation', element: <HotelAutomationPage /> },
      { path: 'hotels/nights', element: <HotelNightsAnalyticsPage /> },

      // Cars
      { path: 'cars', element: <CarSearchPage /> },
      { path: 'cars/search', element: <CarSearchPage /> },

      // Duffel NDC
      { path: 'duffel', element: <DuffelSearchPage /> },
      { path: 'duffel/search', element: <DuffelSearchPage /> },

      // Sabre GDS
      { path: 'sabre', element: <SabreIntegrationDashboard /> },
      { path: 'sabre/availability', element: <SabreAvailabilityPage /> },
      { path: 'sabre/pnr-create', element: <SabrePNRCreationPage /> },
      { path: 'sabre/fare-rules', element: <SabreFareRulesPage /> },
      { path: 'sabre/ancillary', element: <SabreAncillaryPage /> },
      { path: 'sabre/queue-automation', element: <Navigate to="/queue-center?tab=reports" replace /> },
      { path: 'sabre/cryptic', element: <SabreCrypticPage /> },
      { path: 'sabre/destination-finder', element: <DestinationFinderPage /> },
      { path: 'sabre/pnr-split', element: <PNRSplitPage /> },

      // Queue Command Center (Unified Queue Management)
      { path: 'queue-center', element: <QueueCommandCenter /> },
      { path: 'ops/command-center', element: <OpsCommandCenterPage /> },
      { path: 'queue-center/print', element: <PrintableWorklistReport /> },

      // Manuals — operations references (PDF viewer)
      { path: 'manuals', element: <ManualsPage /> },

      // Queues & Automation (legacy routes — redirect to Queue Command Center)
      { path: 'automation/queues', element: <Navigate to="/queue-center" replace /> },
      { path: 'automation/queue-actions', element: <QueueActionDashboard /> },
      { path: 'automation/queue-report', element: <QueueActionReport /> },
      { path: 'automation/queue-config', element: <QueueMonitoringDashboard /> },
      { path: 'automation/queue-rules', element: <Navigate to="/queue-center?tab=reports" replace /> },
      { path: 'automation/schedule-changes', element: <Navigate to="/queue-center?tab=schedule-changes" replace /> },
      { path: 'automation/ttl', element: <TTLMonitorDashboard /> },
      { path: 'automation/ticketing', element: <Navigate to="/queue-center" replace /> },

      // Amadeus
      { path: 'amadeus/queue-manager', element: <Navigate to="/queue-center" replace /> },
      { path: 'amadeus/terminal', element: <AmadeusTerminal /> },
      { path: 'amadeus/availability', element: <AmadeusAvailabilityPage /> },
      { path: 'amadeus/pnr-create', element: <AmadeusPNRCreationPage /> },
      { path: 'amadeus/fare-rules', element: <AmadeusFareRulesPage /> },
      { path: 'amadeus/ancillary', element: <AmadeusAncillaryPage /> },
      { path: 'automation/checkin', element: <CheckInTaskView /> },
      { path: 'automation', element: <Navigate to="/queue-center" replace /> },
      { path: 'amadeus/agent', element: <Navigate to="/ai/amadeus-agent" replace /> },

      // Check-In
      { path: 'checkin', element: <Navigate to="/automation/checkin" replace /> },

      // Loyalty & Planning
      { path: 'rtw', element: <RTWPlannerPage /> },
      { path: 'rtw/assistant', element: <RTWAssistantPage /> },
      { path: 'rtw/troubleshoot', element: <RTWTroubleshooterPage /> },
      { path: 'rtw/agent/:ticketId', element: <RTWAgentWorkspace /> },
      { path: 'rtw/fab-intel-map', element: <FabIntelCoverageMap /> },
      { path: 'rtw/shared-links', element: <SharedLinksAdmin /> },
      { path: 'rtw/invite-drafts', element: <RtwInviteDraftsPage /> },
      { path: 'rtw/desk-queue', element: <RtwDeskQueuePage /> },
      { path: 'rtw/fulfilment-review', element: <RTWFulfilmentReviewPage /> },
      { path: 'ai-inbox', element: <AIInboxPage /> },
      { path: 'ai-inbox/:draftId/console', element: <SabreConsolePage /> },
      { path: 'admin/terms-templates', element: <TermsTemplatesAdmin /> },
      { path: 'loyalty', element: <LoyaltyCalculatorPage /> },
      { path: 'loyalty/calc', element: <QuickPointsCalculator /> },
      { path: 'tier-points-calculator', element: <TierPointsCalculatorPage /> },

      // CRM
      //
      // `/crm/*` is canonical for every CRM feature. Bare aliases (`/customers`,
      // `/leads`, etc.) <Navigate replace> to the canonical path so analytics
      // and breadcrumbs see a single URL surface. Mirrors the UCC pattern below.
      // Exceptions: `/suppliers` and `/communication-hub` are NOT aliases —
      // they render different components — so they stay as-is.
      { path: 'crm', element: <CrmHome /> },
      { path: 'crm/customers', element: <CustomerManagementPage /> },
      { path: 'customers', element: <Navigate to="/crm/customers" replace /> },
      { path: 'crm/customers/:id', element: <CustomerDetailPage /> },
      { path: 'customers/:id', element: <CustomerRedirect />},
      { path: 'crm/customers/:id/360', element: <Client360View /> },
      { path: 'customers/:id/360', element: <Customer360Redirect />},
      { path: 'crm/companies', element: <CompanyManagementPage /> },
      { path: 'companies', element: <Navigate to="/crm/companies" replace /> },
      { path: 'crm/companies/:id', element: <CompanyDetailPage /> },
      { path: 'companies/:id', element: <CompanyRedirect />},
      { path: 'companies/:id/edit', element: <CompanyEditRedirect />},
      { path: 'crm/travelers', element: <TravelerManagementPage /> },
      { path: 'travelers', element: <Navigate to="/crm/travelers" replace /> },
      { path: 'crm/travelers/:id', element: <TravelerDetailPage /> },
      { path: 'travelers/:id', element: <TravelerRedirect />},
      { path: 'crm/person/:id', element: <PersonProfilePage /> },
      { path: 'crm/suppliers', element: <SupplierManagementPage /> },
      { path: 'crm/suppliers/:id', element: <SupplierDetailPage /> },
      { path: 'crm/providers', element: <SellersAndProvidersPage /> },
      { path: 'suppliers', element: <SupplierManager /> },
      { path: 'crm/leads', element: <LeadManagementPage /> },
      { path: 'crm/leads/:id', element: <LeadDetailPage /> },
      { path: 'leads', element: <Navigate to="/crm/leads" replace /> },
      { path: 'crm/communication-logs', element: <CommunicationLogsPage /> },
      { path: 'crm/suggested-updates', element: <ContactEnrichmentQueuePage /> },
      { path: 'crm/contact-matching', element: <ContactMatchingPage /> },
      { path: 'crm/whatsapp-notes', element: <WhatsAppNotesPage /> },
      { path: 'communication-logs', element: <Navigate to="/crm/communication-logs" replace /> },
      { path: 'communication-hub', element: <Navigate to="/ucc" replace /> },
      { path: 'crm/documents', element: <DocumentVaultPage /> },
      { path: 'documents', element: <Navigate to="/crm/documents" replace /> },
      { path: 'crm/documents/manager', element: <DocumentManager /> },
      { path: 'documents/manager', element: <Navigate to="/crm/documents/manager" replace /> },
      { path: 'crm/documents/acknowledgements', element: <DocumentAcknowledgement /> },
      { path: 'documents/acknowledgements', element: <Navigate to="/crm/documents/acknowledgements" replace /> },

      // Tasks & Actions
      { path: 'tasks', element: <TaskDashboard /> },
      { path: 'action-plans', element: <ActionPlanLibrary /> },

      // Routines & Automation
      { path: 'routines', element: <RoutinesManager /> },

      // Finance
      { path: 'finance', element: <FinanceDashboardPage /> },
      { path: 'invoices', element: <InvoiceManagementPage /> },
      { path: 'invoices/templates', element: <InvoiceTemplateManagementPage /> },
      { path: 'invoices/new', element: <CreateTripInvoicePage /> },
      { path: 'invoices/:invoiceId/builder', element: <InvoiceBuilderPage /> },
      { path: 'payments', element: <PaymentManagementPage /> },
      { path: 'finance/payment-sources', element: <PaymentSourcesPage /> },
      { path: 'finance/stripe-balance', element: <StripeBalanceSyncPage /> },
      { path: 'finance/stripe-settlement', element: <StripeSettlementWorkbenchPage /> },
      { path: 'finance/payment-economics', element: <PaymentEconomicsWorkbenchPage /> },
      { path: 'finance/stripe-customers', element: <StripeCustomerSyncPage /> },
      { path: 'finance/settlements', element: <SupplierSettlementPage /> },
      { path: 'finance/terminal', element: <TerminalPage /> },
      { path: 'finance/moto', element: <MotoTerminalPage /> },
      { path: 'finance/payment-methods', element: <PaymentMethodsTermsPage /> },
      { path: 'finance/card-vault-fop', element: <CardVaultFopPage /> },
      { path: 'finance/client-charges', element: <ClientChargesPage /> },
      { path: 'finance/payment-links', element: <PaymentLinksAndManageBookingPage /> },
      { path: 'finance/refunds', element: <RefundTrackingPage /> },
      { path: 'finance/aging', element: <PNTAgingDashboard /> },
      { path: 'finance/cashflow', element: <CashFlowProjection /> },
      { path: 'payments/reconciliation', element: <PaymentReconciliationPage /> },
      { path: 'commissions', element: <CommissionTrackingPage /> },
      { path: 'banking', element: <BankingDashboardPage /> },
      { path: 'banking/plaid-oauth', element: <PlaidOAuthReturnPage /> },
      { path: 'banking/categorize', element: <TransactionCategorizer /> },
      { path: 'banking/rules', element: <MerchantRulesManager /> },
      { path: 'banking/categories', element: <CategoryManager /> },
      { path: 'banking/analytics', element: <BankingAnalyticsDashboard /> },
      { path: 'banking/resolutions', element: <TransactionResolutionPage /> },
      { path: 'banking/reconcile', element: <ReconciliationReviewPage /> },

      // Supplier Finance (AerTicket UK Integration)
      { path: 'finance/supplier-invoices', element: <SupplierInvoiceUploadPage /> },
      { path: 'finance/statement-reconciliation', element: <StatementReconciliationPage /> },
      { path: 'finance/supplier-credit', element: <SupplierCreditDashboard /> },
      { path: 'finance/supplier-config', element: <SupplierConfigurationPage /> },
      { path: 'finance/commission-engine', element: <CommissionEngine /> },

      // General Ledger
      { path: 'finance/chart-of-accounts', element: <ChartOfAccountsPage /> },
      { path: 'finance/journal-entries', element: <JournalEntriesPage /> },

      // Revenue Management
      { path: 'revenue', element: <RevenueManagementDashboard /> },
      { path: 'revenue/dashboard', element: <SearchPreservingRedirect to="/revenue" /> },

      // Reports — single unified Reporting Center. Access to individual
      // categories is enforced per-user via role_permissions (reports:<id>).
      { path: 'reports', element: <ReportingCenter /> },
      { path: 'reports/:category', element: <ReportingCenter /> },
      { path: 'reports/dashboard', element: <Navigate to="/reports/business" replace /> },
      { path: 'reports/builder', element: <Navigate to="/reports/business" replace /> },

      // Fora Integration
      { path: 'finance/fora', element: <ForaIntegrationPage /> },

      // Risk & Alerts
      { path: 'risk', element: <TravelRiskDashboard /> },
      { path: 'risk/dashboard', element: <SearchPreservingRedirect to="/risk" /> },
      { path: 'alerts', element: <SearchPreservingRedirect to="/alerts/flights" /> },
      { path: 'alerts/flights', element: <FlightAlertsSystem /> },

      // Expenses
      { path: 'expenses', element: <ExpenseManager /> },
      { path: 'expenses/manager', element: <SearchPreservingRedirect to="/expenses" /> },

      // Compliance
      { path: 'compliance', element: <AuditTrail /> },
      { path: 'compliance/audit', element: <AuditTrail /> },

      // Operations
      { path: 'inbox', element: <Navigate to="/ops/inbox" replace /> },
      { path: 'ops/inbox', element: <OpsInboxWithAssign /> },
      { path: 'ops/reply-queue', element: <OpsReplyQueuePage /> },
      { path: 'ops/today', element: <TodayPage /> },
      { path: 'ops/waiting', element: <OpsAwaitingReplyPage /> },
      { path: 'ops/lift-and-shift', element: <LiftShiftPage /> },
      { path: 'calls', element: <CallsPage /> },
      { path: 'calls/health', element: <CallsHealthPage /> },
      { path: 'ops/identity-review', element: <OpsIdentityReviewPage /> },
      {
        path: 'ops/dispatch',
        element: (
          <OpsDispatchWithHeader>
            <OpsInboxPage initialLayout="workflow" routePurpose="dispatch" />
          </OpsDispatchWithHeader>
        ),
      },
      {
        path: 'ops/control-tower',
        element: <OpsInboxPage initialLayout="workflow" initialPanel="control-tower" routePurpose="control_tower" />,
      },
      { path: 'ops/handbook', element: <SystemManualPage initialEntryId="aoc-ops-command" pageTitle="AOC + Ops Handbook" entryIds={['aoc-ops-command', 'live-platform-visual-manual']} /> },
      { path: 'ops/customer-requests', element: <OpsCustomerCareRequestInboxPage /> },
      { path: 'ops/stale-work', element: <OpsStaleWorkReviewPage /> },
      { path: 'operations/queue', element: <Navigate to="/queue-center" replace /> },
      { path: 'operations/promises-and-passports', element: <PromisesAndPassportExpiryPage /> },
      { path: 'itinerary/builder', element: <ItineraryBuilderPage /> },
      { path: 'itinerary/advanced', element: <SearchPreservingRedirect to="/proposals" /> },
      { path: 'proposals', element: <AdvancedItineraryBuilder /> },
      { path: 'proposals/tracking', element: <ProposalTrackingDashboard /> },
      { path: 'pnr/history', element: <PNRHistoryPage /> },
      { path: 'pnt', element: <PNTListPage /> },
      { path: 'pnt/obligations', element: <PNTObligationQueuePage /> },
      { path: 'pnt/analytics', element: <PNTAnalyticsDashboard /> },
      { path: 'pnt/plans', element: <PntIssuancePlansPage /> },
      { path: 'pnt/outlook', element: <DailyOutlookPage /> },
      { path: 'pnt/digest-settings', element: <PntDigestSettingsPage /> },
      { path: 'interface-import', element: <InterfaceImportPage /> },
      { path: 'interface-files', element: <InterfaceFileMonitoringPage /> },
      { path: 'sabre/terminal', element: <SabreTerminalPage /> },
      { path: 'sabre/diagnostics', element: <SabreSessionDiagnosticsPage /> },

      // Trip Management
      { path: 'trips', element: <TripManagementPage /> },
      { path: 'trips/:id', element: <TripDetailPage /> },
      { path: 'trips/:id/edit', element: <TripEditPage /> },
      { path: 'trips/:id/statement', element: <TripStatementView /> },

      // Partner Management
      { path: 'partners', element: <PartnerManagementPage /> },
      { path: 'partners/new', element: <PartnerOnboardingWizard /> },
      { path: 'partners/payouts', element: <PartnerPayoutsPage /> },
      { path: 'partners/:id', element: <PartnerDetailPage /> },
      { path: 'partners/:id/commission/new', element: <PartnerCommissionNewRoute /> },
      { path: 'partners/:id/commission/:modelId', element: <PartnerCommissionModelRoute /> },
      { path: 'partners/:id/permissions', element: <PartnerPermissionsRoute /> },

      // Support & Communications — UCC (Unified Communications Center)
      // Canonical path is /ucc. /communications is kept as a permanent alias so
      // existing Freshdesk-era bookmarks and deep links continue to work.
      { path: 'ucc', element: <UnifiedCommsDashboard /> },
      { path: 'communications', element: <Navigate to="/ucc" replace /> },
      { path: 'freshdesk', element: <FreshdeskDashboardPage /> },
      { path: 'freshdesk/sync', element: <FreshdeskTicketSyncPage /> },
      { path: 'freshdesk/analyze', element: <FreshdeskTicketAnalysisPage /> },

      // Documents
      { path: 'documents/generate', element: <PNRDocumentGenerator /> },

      // Settings
      { path: 'settings/gds', element: <GDSSettingsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'settings/personas', element: <AiPersonasAndShiftsManager /> },
      { path: 'admin/personas', element: <AiPersonasAndShiftsManager /> },
      { path: 'ai-manual', element: <TeamAiVisualManualPage /> },
      { path: 'admin/ai-manual', element: <TeamAiVisualManualPage /> },
      { path: 'settings/connections', element: <ConnectionsSettings /> },
      { path: 'settings/sabre-agent', element: <SabreAgentSettingsPage /> },
      { path: 'settings/communications', element: <CommunicationsSettingsPage /> },
      { path: 'settings/automations', element: <AutomationsSettings /> },
      { path: 'settings/api-access', element: <PlatformAccessTokensPage /> },
      // One page with two sections (the team summary, and the message each person gets); the old address of the second opens it at that section.
      { path: 'settings/next-work-digest', element: <MorningSettingsPage /> },
      { path: 'settings/morning-message', element: <Navigate to="/settings/next-work-digest#each-person" replace /> },
      { path: 'settings/schedule-changes', element: <ScheduleChangesSettings /> },
      { path: 'api-docs', element: <ApiDocsPage /> },
      { path: 'automations/fora-sync', element: <ForaSyncPage /> },
      { path: 'crm/hotels', element: <HotelPropertyManagementPage /> },

      // Integrations
      { path: 'integrations', element: <IntegrationsManagementPage /> },
      { path: 'integrations/todoist', element: <TodoistIntegrationPage /> },
      { path: 'integrations/pipedream', element: <PipedreamIntegrationPage /> },
      { path: 'integrations/:id', element: <IntegrationDetailPage /> },

      // Admin
      { path: 'admin', element: <AdminDashboardPage /> },
      { path: 'admin/portal-profiles', element: <PortalProfilesPage /> },
      { path: 'admin/users', element: <UserManagementPage /> },
      { path: 'admin/approvals', element: <UserApprovalQueue /> },
      { path: 'admin/permissions', element: <PermissionCenter /> },
      { path: 'admin/email-templates', element: <EmailTemplatesPage /> },
      { path: 'admin/loyalty-programs', element: <LoyaltyProgramsPage /> },
      { path: 'admin/perk-programs', element: <PerkProgramAdmin /> },
      { path: 'admin/earning-rules', element: <EarningRulesPage /> },
      { path: 'admin/earning-charts', element: <EarningChartEditor /> },
      { path: 'admin/pnr-taxonomy', element: <PnrTaxonomyAdminPage /> },
      { path: 'admin/calculation-tester', element: <CalculationTesterPage /> },
      { path: 'admin/audit-log', element: <AuditLogPage /> },
      { path: 'admin/rbac', element: <RBACManagementPage /> },
      { path: 'admin/web-scraper', element: <WebScraperManager /> },
      { path: 'admin/login-cms', element: <LoginScreenCMS /> },
      { path: 'admin/airline-classes', element: <AirlineClassMappingPage /> },
      { path: 'admin/pbx-routing', element: <PBXConfigPanel /> },
      { path: 'admin/pax-banners', element: <PaxBannersAdmin /> },
      { path: 'admin/pax-content', element: <PaxContentManager /> },
      { path: 'admin/pax-invite', element: <PaxInviteAdmin /> },
      { path: 'admin/sla-dashboard', element: <SLADashboard /> },
      { path: 'admin/system-health', element: <SystemHealthDashboard /> },
      { path: 'admin/system-manual', element: <SystemManualPage /> },
      { path: 'admin/fd-import', element: <FDImportMonitor /> },
      { path: 'admin/crm-tags', element: <CrmTagsAdmin /> },
      // UCC child routes (canonical) + /communications aliases for backward compat.
      { path: 'ucc/new', element: <NewTicketPage /> },
      { path: 'ucc/:displayId', element: <TicketDetailPage /> },
      { path: 'communications/new', element: <Navigate to="/ucc/new" replace /> },
      { path: 'communications/:displayId', element: <CommunicationsTicketRedirect /> },

      // Analytics
      { path: 'analytics', element: <Navigate to="/reports" replace /> },
      { path: 'analytics/agents', element: <AgentPerformancePage /> },
      { path: 'analytics/queue', element: <Navigate to="/queue-center?tab=reports" replace /> },

      // Parent Breadcrumb Redirects (D-IA-02)
      { path: 'ai', element: <Navigate to="/ai-assistant" replace /> },
      { path: 'ai/hub', element: <Navigate to="/ai-hub" replace /> },
      { path: 'amadeus', element: <Navigate to="/amadeus/availability" replace /> },
      { path: 'ops', element: <Navigate to="/ops/today" replace /> },
      { path: 'itinerary', element: <SearchPreservingRedirect to="/itinerary/builder" /> },
      { path: 'automations', element: <Navigate to="/admin/automations" replace /> },
      { path: 'cases', element: <Navigate to="/ucc" replace /> },
      { path: 'cases/:displayId', element: <CommunicationsTicketRedirect /> },
      { path: 'air', element: <Navigate to="/air/quote-research" replace /> },
      { path: 'deadlines', element: <Navigate to="/bookings/deadlines" replace /> },
      { path: 'hotels/quotes', element: <Navigate to="/hotels/automation" replace /> },

      // Data Quality
      { path: 'data-quality', element: <OrphanRecordsDashboard /> },
      { path: 'data-quality/orphans', element: <OrphanRecordsDashboard /> },

      // Templates
      { path: 'templates', element: <ItineraryTemplatesPage /> },

      // ========== 25 IMPROVEMENTS PLAN - NEW ROUTES ==========

      // Task Management System (Feature #16)
      { path: 'tasks/management', element: <TaskManagementDashboard /> },

      // QC Checklists System (Feature #17)
      { path: 'qc-checklists', element: <QCChecklistDashboard /> },
      { path: 'quality-control', element: <Navigate to="/qc-checklists" replace /> },

      // AR Aging Dashboard (Feature #11)
      { path: 'finance/ar-aging', element: <ARAgingDashboard /> },

      // AP Management (Feature #12)
      { path: 'finance/ap-management', element: <APManagementDashboard /> },

      // Supplier Rates Manager (Feature #7)
      { path: 'suppliers/rates', element: <SupplierRatesManager /> },

      // Agent Performance Dashboard (Feature #23)
      { path: 'analytics/agent-performance', element: <AgentPerformanceDashboard /> },



      // Trip Files Manager (Feature #1)
      { path: 'trip-files', element: <TripFilesManager /> },
      { path: 'documents/trip-files', element: <Navigate to="/trip-files" replace /> },

      // Document Templates Manager (Feature #2)
      { path: 'templates/documents', element: <DocumentTemplatesManager /> },

      // Email Templates Manager (Feature #3)
      { path: 'templates/emails', element: <EmailTemplatesManager /> },

      // Smart Alerts Dashboard (Feature #4)
      { path: 'alerts/smart', element: <Navigate to="/smart-alerts" replace /> },
      { path: 'smart-alerts', element: <SmartAlertsDashboard /> },

      // Group Booking Manager (Feature #5)
      { path: 'groups', element: <GroupBookingManager /> },
      { path: 'bookings/groups', element: <Navigate to="/groups" replace /> },

      // Service Fees Manager (Feature #8)
      { path: 'finance/service-fees', element: <ServiceFeesManager /> },

      // Payment Plans Manager (Feature #9)
      { path: 'finance/payment-plans', element: <PaymentPlansManager /> },

      // Refund Management Page (Feature #10)
      { path: 'finance/refund-management', element: <Navigate to="/finance/refunds" replace /> },

      // Revenue Analytics Dashboard (Feature #13)
      { path: 'revenue/analytics', element: <RevenueAnalyticsDashboard /> },

      // Customer Segments Manager (Feature #14)
      { path: 'crm/segments', element: <CustomerSegmentsManager /> },
      { path: 'customers/segments', element: <Navigate to="/crm/segments" replace /> },

      // VIP Support Queue (Feature #16)
      { path: 'crm/vip', element: <Navigate to="/vip-support" replace /> },
      { path: 'vip-support', element: <VIPSupportPage /> },



      // Knowledge Base
      { path: 'knowledge-base', element: <KBAdminPage /> },
      { path: 'admin/kb', element: <Navigate to="/knowledge-base" replace /> },

      // Aviation Operations Center (AOC)
      { path: 'aoc', element: <AviationOperationsCenterPage /> },
      { path: 'operations-center', element: <Navigate to="/aoc" replace /> },

      // PNR Completion Rules (Admin)
      { path: 'admin/pnr-rules', element: <PNRCompletionRulesPage /> },

      // Booking Queue Monitor (Admin)
      { path: 'admin/booking-queue', element: <BookingQueueMonitor /> },
      { path: 'admin/supplier-pollers', element: <SupplierPollerMonitor /> },
      { path: 'admin/platform-events', element: <PlatformEventsPage /> },
      { path: 'admin/automations', element: <AutomationsPage /> },
      { path: 'admin/delivery', element: <DeliveryPage /> },
      { path: 'admin/assistant-controls', element: <DeskAssistantControlsPage /> },
      { path: 'admin/sabre-pool', element: <SabreSessionPoolPage /> },
      { path: 'admin/rtw-fare-rules', element: <RtwFiledFareRulesPage /> },
      { path: 'admin/gds-remark-review', element: <GdsRemarkReviewPage /> },
      { path: 'admin/email-domain-repairs', element: <EmailDomainRepairsPage /> },
      { path: 'admin/managed-lists', element: <ManagedListsPage /> },
      { path: 'admin/workforce', element: <WorkforceAndPayrollAdminPage /> },
      { path: 'admin/manual', element: <TeamAiVisualManualPage /> },
      { path: 'manuals/team-guide', element: <TeamAiVisualManualPage /> },

    ],
  },
  {
    path: '/propeller',
    element: <PropellerProtectedRoute />,
    children: [
      { index: true, element: null },
      { path: 'availability', element: <PropellerAvailabilityPage /> },
      { path: 'fare-quote', element: <PropellerFareQuotePage /> },
      { path: 'price-explorer', element: <PropellerPriceExplorerPage /> },
      { path: 'seat-map', element: <PropellerPaxSeatMapPage /> },
      { path: 'search', element: <PropellerFlightSearchPage /> },
      { path: 'seat-maps', element: <PropellerSeatMapPage /> },
      { path: 'alerts', element: <PropellerAvailabilityAlertsPage /> },
      { path: 'flifo', element: <PropellerFLIFOPage /> },
      { path: 'mct', element: <PropellerMCTCheckerPage /> },
      { path: 'award-search', element: <AwardSearchPage /> },
      { path: 'profile', element: <PropellerProfilePage /> },
      { path: 'settings', element: <PropellerSettingsPage /> },
    ],
  },
  // Keep unregistered paths outside the authenticated route trees. A matched
  // route still reaches its authorization guard; only paths with no route
  // definition render the existing not-found view.
  {
    path: '*',
    element: <ComingSoonPage title="Page Not Found" description="The page you are looking for does not exist or has moved." />,
  },
]);
